import { Fragment, useEffect, useMemo, useState } from "react";
import withMonthlyPaymentsAccess from "../components/withMonthlyPaymentsAccess";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import FileUpload from "../../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../../DesignSystem/Upload/UploadedFileList";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import { ReactComponent as EditIcon } from "../../../../assets/icons/finance/Edit.svg";
import { formatMonthKey } from "../utils/chargeDates";
import { downloadStatementTemplate } from "../utils/statementTemplates";
import {
  FINPLAN_TEMPLATE_HEADERS,
  formatFinplanSplit,
  formatFinplanDate,
  parseFinplanStatementFile,
} from "../utils/finplanStatementParser";
import {
  saveFinplanAdviserLinks,
  saveFinplanStatement,
  subscribeFinplanStatement,
} from "../services/finplanStatementService";
import { linkFinplanStatementRows } from "../utils/finplanAdviserLinks";

const normaliseName = (value) => String(value ?? "").trim().toLowerCase();

const FinplanSummary = ({ selectedMonth, monthOptions, onMonthChange }) => {
  const { advisers } = useAdviserList();
  const selectedMonthLabel = useMemo(
    () => formatMonthKey(selectedMonth),
    [selectedMonth],
  );
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [savingAdviser, setSavingAdviser] = useState(false);
  const [adviserSaveError, setAdviserSaveError] = useState("");

  const tableStatement = useMemo(() => {
    if (!statement) return null;
    const rows = linkFinplanStatementRows({
      rows: statement.rows,
      advisers,
      recipientLinks: statement.recipientLinks,
      sellingAdviserLinks: statement.sellingAdviserLinks,
    });
    const headers = statement.headers.filter(
      (header) =>
        !["recipient email", "selling adviser email"].includes(
          header.toLowerCase(),
        ),
    );
    return { ...statement, headers, rows };
  }, [advisers, statement]);
  const adviserOptions = useMemo(
    () =>
      advisers
        .map((adviser) => ({
          id: adviser.email,
          name: `${adviser.name} (${adviser.email})`,
        }))
        .sort((left, right) => left.name.localeCompare(right.name, "en-GB")),
    [advisers],
  );
  const isAmountColumn = (header) =>
    ["payment amount", "gross received"].includes(header.toLowerCase());
  const statementTotals = useMemo(
    () =>
      (tableStatement?.rows || []).reduce(
        (totals, row) => {
          ["Payment Amount", "Gross Received"].forEach((header) => {
            const amount = Number(
              String(row[header] ?? "").replace(/[,£]/g, ""),
            );
            if (Number.isFinite(amount)) totals[header] += amount;
          });
          return totals;
        },
        { "Payment Amount": 0, "Gross Received": 0 },
      ),
    [tableStatement],
  );
  const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    setStatement(null);
    return subscribeFinplanStatement(
      selectedMonth,
      (nextStatement) => {
        setStatement(nextStatement);
        setLoading(false);
      },
      () => {
        setLoadError("Unable to load the Finplan statement.");
        setLoading(false);
      },
    );
  }, [selectedMonth]);

  const closeUpload = () => {
    if (uploading) return;
    setIsUploadOpen(false);
    setSelectedFile(null);
    setUploadError("");
  };

  const uploadStatement = async () => {
    setUploading(true);
    setUploadError("");
    try {
      const parsed = await parseFinplanStatementFile(selectedFile, selectedMonth);
      await saveFinplanStatement({
        month: selectedMonth,
        file: selectedFile,
        previousStoragePath: statement?.storagePath || "",
        ...parsed,
      });
      setIsUploadOpen(false);
      setSelectedFile(null);
    } catch (error) {
      setUploadError(error.message || "Unable to import the selected statement.");
    } finally {
      setUploading(false);
    }
  };

  const changeLinkedAdviser = async ({ field, sourceName, email }) => {
    const adviser = advisers.find(
      (item) => normaliseName(item.email) === normaliseName(email),
    );
    if (!adviser || !sourceName) return;
    const currentLinks = statement?.[field] || [];
    const nextLinks = currentLinks.filter(
      (link) => normaliseName(link.sourceName) !== normaliseName(sourceName),
    );
    nextLinks.push({
      sourceName,
      adviserName: adviser.name,
      email: adviser.email,
    });
    setSavingAdviser(true);
    setAdviserSaveError("");
    try {
      await saveFinplanAdviserLinks({
        month: selectedMonth,
        field,
        links: nextLinks,
      });
    } catch {
      setAdviserSaveError("Unable to update the linked adviser email.");
    } finally {
      setSavingAdviser(false);
    }
  };

  return (
    <section
      aria-label="Finplan"
      data-month={selectedMonth}
      className="column gap-l quilter-summary"
    >
      <header className="row space-between align-center gap-l">
        <h3 className="FP_H3">Finplan</h3>
        <div className="row align-center gap-s">
          <SecondaryButton
            type="button"
            label="DOWNLOAD TEMPLATE"
            onClick={() =>
              downloadStatementTemplate({
                headers: FINPLAN_TEMPLATE_HEADERS,
                fileName: "Finplan_Statement_Template.xlsx",
                sheetName: "Finplan Statement",
                matchFciStyle: true,
                dateHeaders: ["Payment Created"],
              })
            }
          />
          <PrimaryButton
            type="button"
            label={statement ? "REUPLOAD FINPLAN STATEMENT" : "UPLOAD FINPLAN STATEMENT"}
            onClick={() => setIsUploadOpen(true)}
          />
        </div>
      </header>
      <div role="group" aria-label="Finplan month">
        <RadioGroup
          name="finplan-month"
          options={monthOptions}
          value={selectedMonth}
          onChange={(event) => onMonthChange(event.target.value)}
          inline
        />
      </div>
      {loading && <p className="FP_Content" role="status">Loading Finplan statement...</p>}
      {!loading && loadError && <p className="FP_Content text-error" role="alert">{loadError}</p>}
      {adviserSaveError && <p className="FP_Content text-error" role="alert">{adviserSaveError}</p>}
      {!loading && !loadError && !statement && (
        <p className="FP_Content" role="status">
          No Finplan statement has been uploaded for {selectedMonthLabel}.
        </p>
      )}
      {!loading && statement && (
        <>
          <p className="FP_Help_Text black">
            <a
              className="text-primary-1 quilter-file-link"
              href={statement.fileUrl}
              download={statement.fileName}
            >
              {tableStatement.fileName}
            </a>{" "}
            · {tableStatement.rowCount ?? tableStatement.rows?.length ?? 0} entries
          </p>
          <section className="website-dashboard-card table-scroll-card quilter-statement-card">
            <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
              <table
                className="website-table quilter-statement-table"
                aria-label={`Finplan statement for ${selectedMonthLabel}`}
              >
                <thead>
                  <tr>
                    <th className="FP_Help_Text_Semibold uppercase quilter-action-column">Edit</th>
                    {tableStatement.headers.map((header) => (
                      <th
                        key={header}
                        className={`FP_Help_Text_Semibold uppercase ${isAmountColumn(header) ? "text-align-right" : ""}`}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableStatement.rows.map((row, rowIndex) => (
                    <Fragment key={rowIndex}>
                      <tr
                        className={
                          !row["Recipient Email"] || !row["Selling Adviser Email"]
                            ? "monthly-payment-unlinked-email-row"
                            : ""
                        }
                      >
                        <td className="quilter-action-column">
                          <button
                            type="button"
                            className="table-row-action"
                            aria-label={`Edit adviser links for ${row.Recipient || row["Selling Adviser"] || `row ${rowIndex + 1}`}`}
                            aria-expanded={editingRow === rowIndex}
                            onClick={() => setEditingRow((current) => current === rowIndex ? null : rowIndex)}
                          >
                            <EditIcon aria-hidden="true" />
                          </button>
                        </td>
                        {tableStatement.headers.map((header) => (
                          <td
                            key={header}
                            className={`FP_Help_Text black ${isAmountColumn(header) ? "text-align-right" : ""}`}
                          >
                            {header.toLowerCase() === "payment created"
                              ? formatFinplanDate(row[header], selectedMonth) || "-"
                              : header.toLowerCase() === "split"
                                ? formatFinplanSplit(row[header])
                              : header.toLowerCase() === "business type"
                                ? (
                                  <div className="fit-column gap-xs">
                                    <span>{row[header] || "-"}</span>
                                    {row.FinplanPlanType &&
                                    normaliseName(row.FinplanPlanType) !== normaliseName(row[header]) ? (
                                      <span className="FP_Help_Text text-grey">
                                        {row.FinplanPlanType}
                                      </span>
                                    ) : null}
                                  </div>
                                )
                              : header.toLowerCase() === "recipient"
                                ? (
                                  <div className="fit-column gap-xs">
                                    <span>{row[header] || "-"}</span>
                                    <span className="FP_Help_Text text-grey">
                                      {row["Recipient Email"] || "Email not linked"}
                                    </span>
                                  </div>
                                )
                                : header.toLowerCase() === "selling adviser"
                                  ? (
                                    <div className="fit-column gap-xs">
                                      <span>{row[header] || "-"}</span>
                                      <span className="FP_Help_Text text-grey">
                                        {row["Selling Adviser Email"] || "Email not linked"}
                                      </span>
                                    </div>
                                  )
                                  : row[header] || "-"}
                          </td>
                        ))}
                      </tr>
                      {editingRow === rowIndex ? (
                        <tr className="quilter-edit-row">
                          <td colSpan={tableStatement.headers.length + 1}>
                            <div className="quilter-adviser-edit finplan-adviser-edit">
                              <div className="finplan-adviser-edit-field">
                                <span className="FP_Help_Text_Semibold">
                                  Recipient: {row.Recipient || "-"}
                                </span>
                                <CustomSelect
                                  options={adviserOptions}
                                  value={row["Recipient Email"]}
                                  placeholder="Select recipient adviser email"
                                  disabled={savingAdviser || !row.Recipient}
                                  onChange={(event) => changeLinkedAdviser({
                                    field: "recipientLinks",
                                    sourceName: row.Recipient,
                                    email: event.target.value,
                                  })}
                                />
                              </div>
                              <div className="finplan-adviser-edit-field">
                                <span className="FP_Help_Text_Semibold">
                                  Selling Adviser: {row["Selling Adviser"] || "-"}
                                </span>
                                <CustomSelect
                                  options={adviserOptions}
                                  value={row["Selling Adviser Email"]}
                                  placeholder="Select selling adviser email"
                                  disabled={savingAdviser || !row["Selling Adviser"]}
                                  onChange={(event) => changeLinkedAdviser({
                                    field: "sellingAdviserLinks",
                                    sourceName: row["Selling Adviser"],
                                    email: event.target.value,
                                  })}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="FP_Help_Text_Semibold">Total</td>
                    {tableStatement.headers.map((header) => (
                      <td
                        key={header}
                        className={`FP_Help_Text_Semibold ${isAmountColumn(header) ? "text-align-right" : ""}`}
                      >
                        {isAmountColumn(header)
                          ? formatCurrency(statementTotals[header])
                          : ""}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        </>
      )}

      {isUploadOpen && (
        <div
          className="quilter-upload-overlay"
          role="presentation"
          onMouseDown={(event) => event.target === event.currentTarget && closeUpload()}
        >
          <div
            className="quilter-upload-modal column gap-l"
            role="dialog"
            aria-modal="true"
            aria-labelledby="finplan-upload-title"
          >
            <div className="row space-between align-center gap-m">
              <div>
                <h3 id="finplan-upload-title" className="FP_H3">
                  {statement ? "Reupload" : "Upload"} Finplan statement
                </h3>
                <p className="FP_Content">
                  This statement will be saved against {selectedMonthLabel}.
                </p>
              </div>
              <button
                type="button"
                className="quilter-upload-close"
                onClick={closeUpload}
                aria-label="Close upload dialog"
              >
                ×
              </button>
            </div>
            <FileUpload
              label="Statement file"
              description="Drag and drop a CSV or Excel file here or"
              browseText="Browse files"
              accept=".csv,.xlsx,.xls"
              files={selectedFile ? [selectedFile] : []}
              onFilesChange={(file) => {
                setSelectedFile(file);
                setUploadError("");
              }}
              disabled={uploading}
              status={uploadError ? "error" : "default"}
              statusMessage={uploadError}
            />
            {selectedFile && (
              <UploadedFileList
                files={[selectedFile]}
                onRemove={() => setSelectedFile(null)}
              />
            )}
            <div className="row justify-flex-end gap-m">
              <PrimaryButton
                type="button"
                label={uploading ? "IMPORTING..." : statement ? "REUPLOAD" : "UPLOAD"}
                onClick={uploadStatement}
                disabled={!selectedFile || uploading}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default withMonthlyPaymentsAccess(FinplanSummary);
